import { getDecodedClipboardData } from '@src/features/LogicMapper/reducers/clipboardUtils';

export type ParsedKemuSnippetCode = {
  code: string;
  preview?: string;
  alt?: string;
  width?: string;
  height?: string;
  label?: string;
  controls?: boolean;
}

/**
 * Parses a Kemu snippet code block from a string. Eg:
 * 
 * ```kemu-snippet {
 *   preview: /docs/images/snippet1.jpg
 *   width: 300px
 *   height: 200px
 *   controls: true
 * }
 * KMU-CB-v1%7B%22%7B#....
 * ```
 * 
 * Supported attributes:
 * - preview: URL or path to preview image/video
 * - alt: Alt text for the preview
 * - width: Width of the preview
 * - height: Height of the preview
 * - label: Label text displayed with the snippet
 * - controls: Boolean to enable/disable native video controls (for video previews only)
 *   - Default: enabled (true) for video previews
 *   - Accepts: 'true' (enabled) or 'false' (disabled)
 *   - When enabled, video controls allow play/pause/scrub/volume/fullscreen
 *   - Drag & drop still works from other areas of the snippet (snippet icon, video area outside controls)
 * 
 * @param rawContent - The raw content of the Kemu snippet code block.
 * @returns An object containing the parsed code, preview image, alt text, dimensions, and controls setting.
 */
export const parseKemuSnippet = (rawContent: string): ParsedKemuSnippetCode => {
  const result: ParsedKemuSnippetCode = { code: '' };
  const trimmedContent = rawContent.trimStart(); // Keep trimming start to check for '{'

  if (trimmedContent.startsWith('{')) {
    const closingBraceIndex = trimmedContent.indexOf('}');
    if (closingBraceIndex > 0) {
      const propertiesString = trimmedContent.substring(1, closingBraceIndex).trim();
      // Trim the code part *only* when properties are present
      result.code = trimmedContent.substring(closingBraceIndex + 1).trim();

      // Parse properties
      propertiesString.split(';').forEach(propPair => {
        const trimmedPair = propPair.trim();
        if (!trimmedPair) { return; } // Skip empty parts

        const colonIndex = trimmedPair.indexOf(':');
        if (colonIndex > 0) {
          const key = trimmedPair.substring(0, colonIndex).trim();
          const value = trimmedPair.substring(colonIndex + 1).trim();

          if (key === 'preview') {
            result.preview = value;
          } else if (key === 'alt') {
            result.alt = value;
          } else if (key === 'width') {
            result.width = value;
          } else if (key === 'height') {
            result.height = value;
          } else if (key === 'label') {
            result.label = value;
          } else if (key === 'controls') {
            // Parse boolean
            result.controls = value.toLowerCase() === 'true';
          }
          // Note: Currently, other CSS properties are not stored.
          // If needed, they could be added to a generic 'styles' object in ParsedCode.
        }
      });

      // Handle case where code might be empty after properties
      if (!result.code && propertiesString && closingBraceIndex === trimmedContent.length -1) {
         result.code = ''; // Ensure code is empty string if nothing follows '}' and properties were found
      }

    } else {
      // Malformed properties block (no closing brace), treat entire content as code
      // Keep original rawContent here
      result.code = rawContent;
    }
  } else {
    // No properties block found, entire content is code
    // Keep original rawContent, preserving whitespace
    result.code = rawContent;
  }

  // Fallback if code extraction somehow failed but properties were found
  // If properties were found (keys > 1), the code should be trimmed or empty
  if (Object.keys(result).length > 1 && !result.code) {
      result.code = ''; // Ensure it's an empty string if properties parsed but no code followed
  } else if (Object.keys(result).length === 1 && !trimmedContent.startsWith('{')) {
      // If only 'code' key exists and it didn't start with '{', use rawContent
     result.code = rawContent;
  }


  if (result.code) {
    const decoded = getDecodedClipboardData(result.code);
    if (decoded) {
      result.code = decoded;
    }
  }

  return result;
};
